import React, { useState } from 'react';
import { Copy, Download, RotateCcw, Palette, Check, Settings, Eye } from 'lucide-react';

interface CSSFormatterOptions {
  indentSize: number;
  useTabs: boolean;
  insertFinalNewline: boolean;
  trimTrailingWhitespace: boolean;
  insertSpaceAfterColon: boolean;
  insertSpaceBeforeBrace: boolean;
  newlineAfterSelector: boolean;
  compactMode: boolean;
  sortProperties: boolean;
  uppercaseHex: boolean;
}

const CSSFormatter: React.FC = () => {
  const [inputCode, setInputCode] = useState<string>('');
  const [formattedCode, setFormattedCode] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [options, setOptions] = useState<CSSFormatterOptions>({
    indentSize: 2,
    useTabs: false,
    insertFinalNewline: true,
    trimTrailingWhitespace: true,
    insertSpaceAfterColon: true,
    insertSpaceBeforeBrace: true,
    newlineAfterSelector: true,
    compactMode: false,
    sortProperties: false,
    uppercaseHex: false,
  });

  const formatCSS = (code: string): void => {
    if (!code.trim()) {
      setFormattedCode('');
      return;
    }

    try {
      let css = code.replace(/\/\*[\s\S]*?\*\//g, ''); // Remove comments temporarily
      const comments: string[] = [];
      
      // Extract and preserve comments
      code.replace(/\/\*[\s\S]*?\*\//g, (match) => {
        comments.push(match);
        css = css.replace(match, `__COMMENT_${comments.length - 1}__`);
        return match;
      });

      // Remove extra whitespace
      css = css.replace(/\s+/g, ' ').trim();
      
      let result = '';
      let inSelector = true;
      let braceCount = 0;
      let currentSelector = '';
      let currentProperties: string[] = [];
      
      for (let i = 0; i < css.length; i++) {
        const char = css[i];
        const nextChar = css[i + 1];
        const prevChar = css[i - 1];
        
        if (char === '{') {
          braceCount++;
          inSelector = false;
          
          if (currentSelector.trim()) {
            if (options.insertSpaceBeforeBrace && !currentSelector.endsWith(' ')) {
              currentSelector += ' ';
            }
            result += currentSelector.trim() + char;
            
            if (!options.compactMode && options.newlineAfterSelector) {
              result += '\n';
            }
          }
          currentSelector = '';
        } 
        else if (char === '}') {
          braceCount--;
          inSelector = true;
          
          // Process accumulated properties
          if (currentProperties.length > 0) {
            if (options.sortProperties) {
              currentProperties.sort();
            }
            
            currentProperties.forEach((prop, index) => {
              if (!options.compactMode) {
                result += getIndentation(1);
              }
              result += prop;
              if (!options.compactMode) {
                result += '\n';
              } else if (index < currentProperties.length - 1) {
                result += ' ';
              }
            });
          }
          
          if (!options.compactMode) {
            result += char + '\n\n';
          } else {
            result += char + ' ';
          }
          
          currentProperties = [];
        }
        else if (char === ';' && !inSelector) {
          const property = getCurrentProperty(css, i);
          if (property.trim()) {
            let formattedProp = formatProperty(property.trim());
            currentProperties.push(formattedProp);
          }
          // Skip to next property
          while (i < css.length && css[i] !== '}' && css[i + 1] !== '}') {
            if (css[i] === ';') break;
            i++;
          }
        }
        else if (inSelector) {
          currentSelector += char;
        }
      }
      
      // Clean up result
      if (options.compactMode) {
        result = result.replace(/\s+/g, ' ').trim();
      } else {
        result = result.replace(/\n\s*\n\s*\n/g, '\n\n'); // Remove excessive newlines
        result = result.replace(/\n+$/g, ''); // Remove trailing newlines
      }
      
      // Restore comments
      comments.forEach((comment, index) => {
        result = result.replace(`__COMMENT_${index}__`, comment);
      });
      
      if (options.trimTrailingWhitespace) {
        result = result.replace(/[ \t]+$/gm, '');
      }
      
      if (options.insertFinalNewline && !result.endsWith('\n')) {
        result += '\n';
      }
      
      setFormattedCode(result.trim());
    } catch (error) {
      setFormattedCode(`Error formatting CSS: ${(error as Error).message}`);
    }
  };

  const getCurrentProperty = (css: string, fromIndex: number): string => {
    let start = fromIndex;
    while (start > 0 && css[start] !== ';' && css[start] !== '{') {
      start--;
    }
    if (css[start] === ';' || css[start] === '{') start++;
    
    return css.substring(start, fromIndex).trim();
  };

  const formatProperty = (property: string): string => {
    const colonIndex = property.indexOf(':');
    if (colonIndex === -1) return property;
    
    let prop = property.substring(0, colonIndex).trim();
    let value = property.substring(colonIndex + 1).trim();
    
    // Format hex colors
    if (options.uppercaseHex) {
      value = value.replace(/#[0-9a-fA-F]{3,6}/g, (match) => match.toUpperCase());
    } else {
      value = value.replace(/#[0-9a-fA-F]{3,6}/g, (match) => match.toLowerCase());
    }
    
    // Add space after colon
    const space = options.insertSpaceAfterColon ? ' ' : '';
    return `${prop}:${space}${value};`;
  };

  const getIndentation = (level: number): string => {
    if (options.useTabs) {
      return '\t'.repeat(level);
    }
    return ' '.repeat(level * options.indentSize);
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(formattedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = (): void => {
    const blob = new Blob([formattedCode], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted-styles.css';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReset = (): void => {
    setInputCode('');
    setFormattedCode('');
  };

  const syntaxHighlight = (code: string): string => {
    if (!code) return '';
    
    return code
      // CSS Properties
      .replace(/(display|position|top|right|bottom|left|width|height|margin|padding|border|background|color|font|text|line-height|z-index|opacity|transform|transition|animation|flex|grid|align|justify|overflow|visibility|cursor|box-shadow|border-radius|outline|min-width|max-width|min-height|max-height)(-[a-z-]+)?:/g,
        '<span style="color: #9cdcfe;">$1$2</span>:')
      // CSS Values and units
      .replace(/:\s*([^;{]+)/g, (match, value) => {
        return ': ' + value
          .replace(/(\d+)(px|em|rem|%|vh|vw|pt|pc|in|cm|mm|ex|ch|vmin|vmax|deg|rad|grad|turn|s|ms|Hz|kHz)/g, 
            '<span style="color: #b5cea8;">$1</span><span style="color: #4fc1ff;">$2</span>')
          .replace(/(#[0-9a-fA-F]{3,6})/g, '<span style="color: #ce9178;">$1</span>')
          .replace(/(rgb|rgba|hsl|hsla)\([^)]+\)/g, '<span style="color: #ce9178;">$1</span>')
          .replace(/\b(auto|none|inherit|initial|unset|normal|bold|italic|center|left|right|top|bottom|middle|baseline|flex-start|flex-end|space-between|space-around|space-evenly|wrap|nowrap|column|row|block|inline|inline-block|flex|grid|absolute|relative|fixed|sticky|static|hidden|visible|scroll|auto)\b/g,
            '<span style="color: #569cd6;">$1</span>');
      })
      // Selectors
      .replace(/^([^{]+)(?=\s*\{)/gm, (match) => {
        return match
          .replace(/([.#][a-zA-Z0-9_-]+)/g, '<span style="color: #d7ba7d;">$1</span>')
          .replace(/([a-zA-Z][a-zA-Z0-9]*)/g, '<span style="color: #4ec9b0;">$1</span>')
          .replace(/(::?[a-zA-Z-]+)/g, '<span style="color: #dcdcaa;">$1</span>');
      })
      // Comments
      .replace(/(\/\*[\s\S]*?\*\/)/g, '<span style="color: #6a9955; font-style: italic;">$1</span>')
      // Strings
      .replace(/("[^"]*"|'[^']*')/g, '<span style="color: #ce9178;">$1</span>')
      // Braces and semicolons
      .replace(/([{}:;,])/g, '<span style="color: #d4d4d4;">$1</span>');
  };

  const updateOption = <K extends keyof CSSFormatterOptions>(key: K, value: CSSFormatterOptions[K]): void => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Palette className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">CSS Code Formatter</h1>
                <p className="text-gray-600">Format and beautify your CSS code with advanced styling options</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4 items-center">
              <button
                onClick={() => formatCSS(inputCode)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Format CSS
              </button>
              
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Options
              </button>
              
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-200 transition-colors font-medium flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              
              <button
                onClick={handleReset}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
            
            {showOptions && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-medium text-gray-900 mb-3">CSS Formatting Options</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Indent Size:</label>
                    <select 
                      value={options.indentSize} 
                      onChange={(e) => updateOption('indentSize', Number(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={2}>2 spaces</option>
                      <option value={4}>4 spaces</option>
                      <option value={8}>8 spaces</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="useTabs"
                      checked={options.useTabs}
                      onChange={(e) => updateOption('useTabs', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="useTabs" className="text-sm font-medium text-gray-700">Use tabs</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="compactMode"
                      checked={options.compactMode}
                      onChange={(e) => updateOption('compactMode', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="compactMode" className="text-sm font-medium text-gray-700">Compact mode</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sortProperties"
                      checked={options.sortProperties}
                      onChange={(e) => updateOption('sortProperties', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="sortProperties" className="text-sm font-medium text-gray-700">Sort properties</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="uppercaseHex"
                      checked={options.uppercaseHex}
                      onChange={(e) => updateOption('uppercaseHex', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="uppercaseHex" className="text-sm font-medium text-gray-700">Uppercase HEX</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="insertSpaceAfterColon"
                      checked={options.insertSpaceAfterColon}
                      onChange={(e) => updateOption('insertSpaceAfterColon', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="insertSpaceAfterColon" className="text-sm font-medium text-gray-700">Space after colon</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="insertSpaceBeforeBrace"
                      checked={options.insertSpaceBeforeBrace}
                      onChange={(e) => updateOption('insertSpaceBeforeBrace', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="insertSpaceBeforeBrace" className="text-sm font-medium text-gray-700">Space before brace</label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="trimTrailingWhitespace"
                      checked={options.trimTrailingWhitespace}
                      onChange={(e) => updateOption('trimTrailingWhitespace', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="trimTrailingWhitespace" className="text-sm font-medium text-gray-700">Trim trailing whitespace</label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="insertFinalNewline"
                      checked={options.insertFinalNewline}
                      onChange={(e) => updateOption('insertFinalNewline', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="insertFinalNewline" className="text-sm font-medium text-gray-700">Final newline</label>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Input CSS</h2>
                <span className="text-sm text-gray-500">{inputCode.length} characters</span>
              </div>
              <textarea
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder="Paste your CSS code here..."
                className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Formatted CSS</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopy}
                    disabled={!formattedCode}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={!formattedCode}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>
              <div className="relative">
                <pre className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm overflow-auto bg-gray-50">
                  <code 
                    dangerouslySetInnerHTML={{ 
                      __html: syntaxHighlight(formattedCode) || '<span class="text-gray-400">Formatted CSS will appear here...</span>' 
                    }}
                  />
                </pre>
              </div>
            </div>
          </div>

          {showPreview && formattedCode && (
            <div className="border-t border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Live Preview</h3>
              <div className="border border-gray-300 rounded-lg p-4 bg-white">
                <style dangerouslySetInnerHTML={{ __html: formattedCode }} />
                <div className="space-y-4">
                  <div className="example-element p-4 border border-dashed border-gray-300 rounded">
                    <h4 className="font-medium mb-2">Preview Element</h4>
                    <p>This element will be styled by your CSS. Add classes and styles to see the preview in action.</p>
                    <button className="mt-2 px-4 py-2 bg-blue-500 text-white rounded">Sample Button</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {formattedCode && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>CSS formatting completed successfully</span>
                <span>{formattedCode.length} characters formatted</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSSFormatter;